import React from 'react';
import { Link, useLocation, useMatch, useResolvedPath } from 'react-router-dom';
import classNames from 'classnames';
import { Text } from 'components/Text';
import { NavItem } from 'types/sidebar';

type SidebarNavLinkProps = {
  item: NavItem;
  setSidebarOpen?: (value: boolean) => void;
  isCollapsed?: boolean;
};

export const SidebarNavLink = ({
  item,
  setSidebarOpen,
  isCollapsed = false,
}: SidebarNavLinkProps) => {
  const location = useLocation();
  const resolvedPath = useResolvedPath(item.to || '');
  const routeMatch = useMatch({
    path: resolvedPath.pathname,
    end: resolvedPath.pathname === '/',
  });
  const pathname = location.pathname.toLowerCase();
  const matchesPath = (path: string) =>
    pathname === path.toLowerCase() ||
    pathname.startsWith(`${path.toLowerCase()}/`);
  const relatedRouteActive = item.activePaths?.some(matchesPath);
  const inactiveRoute = item.inactivePaths?.some(matchesPath);
  const isActive = !inactiveRoute && Boolean(routeMatch || relatedRouteActive);

  if (item.disabled) {
    return (
      <div
        className={classNames(
          'mx-2 rounded-md group flex items-center min-h-[44px] text-[11.9px] leading-4 font-medium cursor-not-allowed opacity-55',
          isCollapsed
            ? 'justify-center px-2 py-2.5'
            : 'justify-between px-3 py-2.5',
          'text-slate-400',
        )}
        title={
          isCollapsed
            ? `${item.name} - ${item.badge || 'Coming soon'}`
            : undefined
        }
        aria-disabled="true"
      >
        <div
          className={classNames(
            'group flex items-center',
            isCollapsed && 'justify-center',
          )}
        >
          <item.icon
            className={classNames(
              'flex-shrink-0 h-6 w-6 text-slate-500',
              !isCollapsed && 'mr-3',
            )}
            aria-hidden="true"
          />
          {!isCollapsed && item.name}
        </div>
        {!isCollapsed && item.badge ? (
          <span className="rounded-full border border-slate-600 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
            {item.badge}
          </span>
        ) : null}
      </div>
    );
  }

  return (
    <Link
      to={item.to || ''}
      aria-current={isActive ? 'page' : undefined}
      aria-label={isCollapsed ? item.name : undefined}
      className={classNames(
        isActive
          ? 'bg-slate-700/50 text-slate-50'
          : 'text-slate-50 hover:bg-slate-700/50',
        isCollapsed
          ? 'mx-2 rounded-md group flex items-center justify-center px-2 py-2.5 min-h-[44px] text-[11.9px] leading-4 font-medium'
          : 'mx-2 rounded-md group flex items-center px-3 py-2.5 min-h-[44px] text-[11.9px] leading-4 font-medium justify-between',
      )}
      onClick={() => {
        if (item.onClick) item.onClick();
        return setSidebarOpen ? setSidebarOpen(false) : null;
      }}
      title={isCollapsed ? item.name : undefined}
    >
      <div
        className={classNames(
          'group flex items-center',
          isCollapsed && 'justify-center',
        )}
      >
        <item.icon
          className={classNames(
            isActive
              ? 'text-slate-50'
              : 'text-slate-400 group-hover:text-slate-200',
            'flex-shrink-0 h-6 w-6',
            !isCollapsed && 'mr-3',
          )}
          aria-hidden="true"
        />
        {!isCollapsed && item.name}
      </div>
      {!isCollapsed && item.count && item.count > 0 ? (
        <div className="bg-slate-500 text-center rounded-full h-5 w-7">
          <Text
            value={`${item.count}`}
            className="leading-loose text-white text-[10px]"
          />
        </div>
      ) : null}
    </Link>
  );
};
